import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AsyncState } from '@/hooks';
import type { WorkflowProcess } from '@/types/workflows';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import solutionMindset from '@/assets/images/undraw_solution-mindset_5xp7.svg';
import { humanizeRefCode } from './approver-labels';
import styles from '../AdminWorkflowsPage.module.css';

/**
 * The process catalogue: every governable action in the product, grouped by the
 * domain that owns it, each card stating IN WORDS what law currently applies —
 * "platform default", "customised", or "no workflow, actions apply immediately".
 * The words are the point: a toggle labelled "active" told administrators
 * nothing about what would actually happen when a bursar files a fee waiver.
 */

const NS = 'admin.workflows';

interface ProcessesTabProps {
  state: AsyncState<WorkflowProcess[]>;
  onRetry: () => void;
}

/**
 * The step count for the card's chain glyph, mined from the free-text
 * stepsSummary ("2 steps — Bursar, then Principal") because the catalogue row
 * carries no structured count. A summary with no digits yields null and the
 * glyph draws a single indeterminate step rather than inventing a number.
 */
function stepCountFrom(process: WorkflowProcess): number | null {
  if (!process.activeVersion) return null;
  const summary = process.activeVersion.stepsSummary;
  if (!summary) return null;
  const match = summary.match(/\d+/);
  return match ? Number(match[0]) : null;
}

export function ProcessesTab({ state, onRetry }: ProcessesTabProps) {
  const { t } = useTranslation();

  return (
    <AsyncPanel
      state={state}
      onRetry={onRetry}
      isEmpty={(data) => data.length === 0}
      empty={
        <EmptyState
          illustration={solutionMindset}
          title={t(`${NS}.processes.empty`)}
          description={t(`${NS}.processes.emptyBody`)}
        />
      }
    >
      {(data) => <ProcessGroups processes={data} />}
    </AsyncPanel>
  );
}

function ProcessGroups({ processes }: { processes: WorkflowProcess[] }) {
  const { t } = useTranslation();

  const groups = useMemo(() => {
    const byModule = new Map<string, WorkflowProcess[]>();
    for (const process of processes) {
      const bucket = byModule.get(process.ownerModule);
      if (bucket) bucket.push(process);
      else byModule.set(process.ownerModule, [process]);
    }
    return [...byModule.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [processes]);

  return (
    <div className={styles.processGroups}>
      {groups.map(([module, entries]) => (
        <section key={module} className={styles.processGroup}>
          <h2 className={styles.processGroupTitle}>
            {t(`${NS}.modules.${module}`, { defaultValue: humanizeRefCode(module) })}
          </h2>
          <div className={styles.processGrid}>
            {entries.map((process) => (
              <ProcessCard key={process.key} process={process} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function ProcessCard({ process }: { process: WorkflowProcess }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stepCount = stepCountFrom(process);

  /** The governance sentence — the single most load-bearing line on the card. */
  const governance = process.activeVersion
    ? t(`${NS}.processes.customised`, { version: process.activeVersion.version })
    : process.platformDefault
      ? t(`${NS}.processes.platformDefault`, { version: process.platformDefault.version })
      : t(`${NS}.processes.none`);

  const open = () => navigate(`/admin/institution/workflows/${encodeURIComponent(process.key)}`);

  return (
    <Card className={styles.processCard}>
      <div className={styles.processCardHead}>
        <div className={styles.processCardText}>
          <h3 className={styles.processCardTitle}>{process.title}</h3>
          {process.description && <p className={styles.processCardDescription}>{process.description}</p>}
        </div>
        <ChainGlyph stepCount={stepCount} governed={process.activeVersion !== null || process.platformDefault !== null} />
      </div>

      <p
        className={
          process.activeVersion
            ? `${styles.governanceLine} ${styles.governanceCustomised}`
            : process.platformDefault
              ? styles.governanceLine
              : `${styles.governanceLine} ${styles.governanceNone}`
        }
      >
        {governance}
        {process.activeVersion?.stepsSummary ? ` — ${process.activeVersion.stepsSummary}` : ''}
      </p>

      <div className={styles.processCardActions}>
        <Button size="sm" onClick={open}>
          {t(`${NS}.processes.customise`)}
        </Button>
        <Button size="sm" variant="secondary" onClick={open}>
          {t(`${NS}.processes.viewVersions`)}
        </Button>
      </div>
    </Card>
  );
}

/* Glyph geometry: a miniature of the designer's chain, drawn left-to-right to
 * fit a card corner. Small enough to hand-place. */
const GLYPH_STEP = 14;
const GLYPH_GAP = 8;
const GLYPH_R = 5;
const GLYPH_H = 22;
const GLYPH_SHOWN_MAX = 3;

/**
 * The card's chain glyph: maker dot → step squares → done tick, a miniature of
 * the pipeline the designer edits full-size. More than three steps compresses
 * into "+n" — the glyph's job is "this action is guarded, this hard", not an
 * exact diagram; the designer holds the exact one. A null count (the catalogue
 * gave no number) draws one dashed, indeterminate step.
 */
function ChainGlyph({ stepCount, governed }: { stepCount: number | null; governed: boolean }) {
  const { t } = useTranslation();

  const shown = stepCount === null ? 1 : Math.max(1, Math.min(stepCount, GLYPH_SHOWN_MAX));
  const overflow = stepCount !== null && stepCount > GLYPH_SHOWN_MAX ? stepCount - GLYPH_SHOWN_MAX : 0;

  const label =
    stepCount !== null
      ? t(`${NS}.glyph.label`, { n: stepCount })
      : governed
        ? t(`${NS}.glyph.labelUnknown`)
        : t(`${NS}.glyph.labelNone`);

  const midY = GLYPH_H / 2;
  let x = GLYPH_R + 2;
  const makerX = x;
  x += GLYPH_R + GLYPH_GAP;
  const stepXs: number[] = [];
  for (let index = 0; index < shown; index += 1) {
    stepXs.push(x);
    x += GLYPH_STEP + GLYPH_GAP;
  }
  const overflowX = overflow > 0 ? x : null;
  if (overflowX !== null) x += GLYPH_STEP + GLYPH_GAP;
  const doneX = x;
  const width = doneX + GLYPH_R + 3;

  return (
    <svg
      className={governed ? styles.glyph : `${styles.glyph} ${styles.glyphUngoverned}`}
      viewBox={`0 0 ${width} ${GLYPH_H}`}
      width={width}
      height={GLYPH_H}
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      <line className={styles.glyphLine} x1={makerX} y1={midY} x2={doneX} y2={midY} />
      <circle className={styles.glyphMaker} cx={makerX} cy={midY} r={GLYPH_R} />
      {stepXs.map((stepX, index) => (
        <rect
          key={stepX}
          className={stepCount === null ? `${styles.glyphStep} ${styles.glyphStepUnknown}` : styles.glyphStep}
          x={stepX}
          y={midY - GLYPH_STEP / 2}
          width={GLYPH_STEP}
          height={GLYPH_STEP}
          rx={3}
          data-step={index + 1}
        />
      ))}
      {overflowX !== null && (
        <text className={styles.glyphOverflow} x={overflowX + GLYPH_STEP / 2} y={midY + 3.5} textAnchor="middle">
          +{overflow}
        </text>
      )}
      <circle className={styles.glyphDone} cx={doneX} cy={midY} r={GLYPH_R} />
      <path
        className={styles.glyphTick}
        d={`M ${doneX - 2.6} ${midY} l 1.9 2 l 3.4 -3.8`}
        fill="none"
      />
    </svg>
  );
}
